/**
 * Bildirim gövdeleri düz metindir: FCM yükü, Android bildirim çekmecesi ve
 * uygulama içi kart aynı `body` alanını gösterir. Sağlayıcıdan gelen Markdown
 * ham hâliyle iletildiğinde ekranda `### ... **...**` yığını oluşuyordu.
 * Burada yapı (başlık, madde, adım) korunur, yalnız işaretler temizlenir.
 * Backend'deki `services/ai_notification_copy.py` aynı sözleşmeyi üretir.
 */

const ANSI = /\x1b\[[0-?]*[ -/]*[@-~]/g
const CONTROL = /[\x00-\x08\x0b\x0c\x0e-\x1f\x7f]/g
const FENCE = /^\s{0,3}(?:```|~~~)/
const HEADING = /^\s{0,3}#{1,6}\s+(.+?)\s*#*\s*$/
const RULE = /^\s{0,3}(?:[-*_])(?:\s*[-*_]){2,}\s*$/
const TABLE_SEPARATOR = /^\s*\|?[\s:|-]*-[\s:|-]*\|?\s*$/
const BULLET = /^(\s*)[-*+]\s+(.*)$/
// Zaten sadeleştirilmiş bir gövde tekrar temizlenebildiği için (geçmiş
// kayıtları, kart yeniden çizimi) `\u2022` maddesi girintisiyle korunur.
const NORMALIZED_BULLET = /^(\s*)\u2022\s+(.*)$/
const NUMBERED = /^(\s*)(\d{1,3})[.)]\s+(.*)$/
const QUOTE = /^\s{0,3}>\s?/
const IMAGE_OR_LINK = /!?\[([^\]\n]*)\]\(\s*<?([^)\s>]*)>?(?:\s+["'][^"']*["'])?\s*\)/g
const AUTOLINK = /<((?:https?|mailto):[^>\s]+)>/g
const CODE_SPAN = /`{1,3}([^`\n]+)`{1,3}/g
const STRONG_EM = /(\*\*\*|___)(?!\s)([\s\S]+?)(?<!\s)\1/g
const STRONG = /(\*\*|__)(?!\s)([\s\S]+?)(?<!\s)\1/g
const EM_STAR = /(?<![\w*])\*(?!\s)([^*\n]+?)(?<![\s*])\*(?![\w*])/g
const EM_UNDERSCORE = /(?<![\w_])_(?!\s)([^_\n]+?)(?<![\s_])_(?![\w_])/g
const STRIKE = /~~(?!\s)([\s\S]+?)(?<!\s)~~/g
const CHECKBOX = /^\[([ xX])\]\s*/
const SENTENCE_END = '.:!?…»)]'

function inlinePlain(text: string): string {
  return text
    .replace(AUTOLINK, '$1')
    // Etiketsiz bağlantıda da hedefin kaybolmaması gerekir.
    .replace(IMAGE_OR_LINK, (_match, label: string, target: string) => label.trim() || target)
    .replace(CODE_SPAN, '$1')
    .replace(STRONG_EM, '$2')
    .replace(STRONG, '$2')
    .replace(EM_STAR, '$1')
    .replace(EM_UNDERSCORE, '$1')
    .replace(STRIKE, '$1')
    .replace(CHECKBOX, (_match, state: string) => (state === ' ' ? '◻ ' : '✓ '))
    .replace(/[ \t]+/g, ' ')
    .trim()
}

function headingLine(title: string): string {
  const trimmed = title.replace(/[\s:]+$/, '')
  return trimmed && !SENTENCE_END.includes(trimmed[trimmed.length - 1]) ? `${trimmed}:` : trimmed
}

/** Markdown yanıtını, yapısı korunmuş okunabilir düz metne çevirir. */
export function notificationPlainText(input: string): string {
  const source = String(input || '').replace(ANSI, '').replace(CONTROL, '').replace(/\r\n?/g, '\n')
  const lines: string[] = []
  let fenced = false
  for (const raw of source.split('\n')) {
    if (FENCE.test(raw)) { fenced = !fenced; continue }
    if (fenced) { lines.push(raw.trimEnd()); continue }
    const unquoted = raw.replace(QUOTE, '')
    const stripped = unquoted.trim()
    if (!stripped || RULE.test(unquoted) || (stripped.includes('|') && TABLE_SEPARATOR.test(unquoted))) {
      lines.push('')
      continue
    }
    const heading = HEADING.exec(unquoted)
    if (heading) {
      const content = inlinePlain(heading[1])
      if (content) { lines.push(''); lines.push(headingLine(content)) }
      continue
    }
    const numbered = NUMBERED.exec(unquoted)
    if (numbered) {
      const content = inlinePlain(numbered[3])
      if (content) lines.push(`${numbered[2]}. ${content}`)
      continue
    }
    const normalized = NORMALIZED_BULLET.exec(unquoted)
    if (normalized) {
      const content = inlinePlain(normalized[2])
      if (content) lines.push(`${normalized[1].length >= 2 ? '  ' : ''}• ${content}`)
      continue
    }
    const bullet = BULLET.exec(unquoted)
    if (bullet) {
      const content = inlinePlain(bullet[2])
      // Tek seviye iç içe madde korunur; daha derini bildirimde okunmaz.
      if (content) lines.push(`${bullet[1].length >= 2 ? '  ' : ''}• ${content}`)
      continue
    }
    const content = inlinePlain(stripped)
    if (content) lines.push(content)
  }
  const collapsed: string[] = []
  for (const line of lines) {
    if (!line && (collapsed.length === 0 || !collapsed[collapsed.length - 1])) continue
    collapsed.push(line)
  }
  while (collapsed.length && !collapsed[collapsed.length - 1]) collapsed.pop()
  return collapsed.join('\n')
}

/** Uzun yanıtı kart için kısaltır; kelimeyi ortasından bölmez. */
export function notificationExcerpt(input: string, maxChars = 700): string {
  const text = notificationPlainText(input)
  if (text.length <= maxChars) return text
  const cut = text.slice(0, maxChars)
  const boundary = Math.max(cut.lastIndexOf('\n'), cut.lastIndexOf(' '))
  const candidate = boundary >= maxChars / 2 ? cut.slice(0, boundary) : cut
  return `${candidate.replace(/[\s.,;:-]+$/, '')}…`
}

export type NotificationBlock =
  | { kind: 'gap' }
  | { kind: 'heading'; text: string }
  | { kind: 'paragraph'; text: string }
  | { kind: 'bullet'; text: string; nested: boolean }
  | { kind: 'step'; marker: string; text: string; details: string[] }

const BLOCK_BULLET = /^(\s*)•\s+(.*)$/
const BLOCK_STEP = /^\s*(\d{1,3})[.)]\s+(.*)$/

/**
 * Temizlenmiş gövdeyi kartta biçimlenebilir bloklara ayırır. Girdi düz metin
 * olduğu için ayrım yalnız yazının kendi ipuçlarına dayanır: `•` madde,
 * `1.` adım, iki nokta ile biten kısa satır başlık.
 */
export function notificationBlocks(body: string): NotificationBlock[] {
  const blocks: NotificationBlock[] = []
  for (const raw of String(body || '').split(/\r\n?|\n/)) {
    if (!raw.trim()) {
      if (blocks.length && blocks[blocks.length - 1].kind !== 'gap') blocks.push({ kind: 'gap' })
      continue
    }
    const step = BLOCK_STEP.exec(raw)
    if (step) {
      // Panel işleri adımları tek satırda ` · ` ile ayrılmış alanlar taşır.
      const [text, ...details] = step[2].split(' · ')
      blocks.push({ kind: 'step', marker: step[1], text: text.trim(), details: details.map(part => part.trim()).filter(Boolean) })
      continue
    }
    const bullet = BLOCK_BULLET.exec(raw)
    if (bullet) {
      blocks.push({ kind: 'bullet', text: bullet[2].trim(), nested: bullet[1].length >= 2 })
      continue
    }
    const line = raw.trim()
    if (line.endsWith(':') && line.length <= 72 && !/[.!?]\s/.test(line)) {
      blocks.push({ kind: 'heading', text: line.slice(0, -1) })
      continue
    }
    blocks.push({ kind: 'paragraph', text: line })
  }
  while (blocks.length && blocks[blocks.length - 1].kind === 'gap') blocks.pop()
  return blocks
}
